import React from "react";

const TAGS = [
  "Explicit Plans",
  "Machine-Checkable Verification",
  "Replay & Resume",
  "Approval-Gated Execution",
  "MCP Integration",
  "Cross-Day Continuity",
];

const BUILT_STACK = [
  "Execution runtime",
  "Verification engine",
  "Memory layer",
  "MCP tooling",
  "Companion UI",
];

const NATIVE_SIGNALS = [
  {
    title: "AppKit + Accessibility",
    description:
      "Inspects real macOS interface hierarchies and works through the same system surfaces operators already use.",
  },
  {
    title: "Browser + DOM Mapping",
    description:
      "Treats browser windows as structured surfaces so bounded web workflows stay inspectable instead of opaque.",
  },
  {
    title: "Foundation Models Substrate",
    description:
      "Uses deterministic prose analysis as one substrate inside a broader runtime, not as a substitute for proof.",
  },
];

const PROOF_POINTS = [
  {
    metric: "163/163",
    title: "Weekly publication pack",
    description:
      "All configured end-to-end gates passed across planning, execution, live web, and learning lanes.",
  },
  {
    metric: "36/36",
    title: "Frozen holdout pack",
    description:
      "A fixed holdout release bar passed cleanly, not just a set of tuned demo paths.",
  },
  {
    metric: "6/6",
    title: "Ambient companion release",
    description:
      "Bounded initiative, active-context review, and resumable-work slices all shipped green.",
  },
  {
    metric: "5/5",
    title: "Web V2 hardening",
    description:
      "Bounded transactional web flows gained stronger inspection, recovery, and proof-backed reliability.",
  },
];

export default function MemoryAgent() {
  return (
    <section id="venture" className="section section--surface">
      <div className="section-inner">
        <div className="venture__header">
          <p className="venture__label">Current Venture</p>
          <h2 className="venture__title">MemoryAgent</h2>
          <p className="venture__summary">
            A macOS-native verifiable AI execution system that turns
            natural-language requests into safe, replayable, evidence-backed
            work. Proof, not promises.
          </p>
          <p className="venture__technical">
            MemoryAgent compiles requests into explicit GoalGraph plans,
            executes across apps, files, and bounded web flows, verifies
            outcomes step-by-step, and stores run artifacts for replay,
            debugging, and trust.
          </p>
        </div>

        <div className="venture__tags">
          {TAGS.map((tag) => (
            <span key={tag} className="venture__tag">{tag}</span>
          ))}
        </div>

        <div className="venture__built">
          <p className="venture__built-title">Built End-to-End</p>
          <div className="venture__built-items">
            {BUILT_STACK.map((item) => (
              <span key={item} className="venture__built-item">
                {item}
              </span>
            ))}
          </div>
        </div>

        <div className="venture__native">
          <p className="venture__native-title">Why the product reads as native</p>
          <div className="venture__native-grid">
            {NATIVE_SIGNALS.map(({ title, description }) => (
              <div key={title} className="native-signal">
                <h3 className="native-signal__title">{title}</h3>
                <p className="native-signal__description">{description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="venture__proof">
          <p className="venture__proof-title">Proof Surfaces</p>
          <p className="venture__proof-subtitle">
            These are release-facing checks tied to visible product behavior,
            backed by stored run artifacts rather than demo-only claims.
          </p>
          <div className="venture__proof-grid">
            {PROOF_POINTS.map(({ metric, title, description }) => (
              <div key={title} className="proof-card">
                <div className="proof-card__metric">{metric}</div>
                <div className="proof-card__title">{title}</div>
                <div className="proof-card__description">{description}</div>
              </div>
            ))}
          </div>
          <p className="venture__proof-note">
            Runs persist verification reports, evidence artifacts, branch
            decisions, and approval records for replay, explainability, and
            debugging.
          </p>
        </div>

        <a
          href="https://memoryagent.dev"
          target="_blank"
          rel="noopener noreferrer"
          className="venture__link"
        >
          Visit memoryagent.dev <span>&rarr;</span>
        </a>
      </div>
    </section>
  );
}
